/**
 * A room's size typed in, as the app's room size popup (`InfoRoomSizePopup`, `ChangeRoomDimension`): the width and the
 * length of the floor (its inner outline's extent along x and along z), its area, the ceiling height and the thickness
 * of its walls.
 *
 * - Width and length move one side of the room (`RoomController.TrySetSize`): the lower side first, the upper one when
 *   that cannot be done; the walls meeting that side stretch with it.
 * - The area scales the room about its middle (`TrySetArea`) — the factor found by bisection so that the floor inside
 *   walls of unchanged thickness comes to the typed area; what stands in the room moves with it, keeping its size.
 * - The ceiling height is the whole floor's, as the app's (`ChangeRoomDimension`: ViewLevel.Height): every room takes it,
 *   the walls of the old height follow, and what hangs from the ceiling goes with it.
 * - The walls thicken outward, the room keeping its floor (`TrySetDepthInRoom(outside: true)`).
 *
 * Doors and windows keep their place in the world on a wall that grows or shrinks, as the app's do, and scale with a
 * room that is scaled.
 */
import type { Item, Level, Room, Vec2 } from '../core/hp/types';
import { applyWallProperties, moveCorners, polygonArea, type EditResult } from '../core/geometry/edit';
import { edgeWalls, floorArea, floorExtent, floorInsets, offsetPolygon } from '../core/geometry/roomFloor';
import { containsPoint } from './sceneGesture';
import { t, type TranslationKey } from '../i18n/I18n';

export interface RoomSizeValues { width?: number; depth?: number; area?: number; height?: number; thickness?: number }
export interface RoomSizes { width: number; depth: number; area: number; height: number; thickness: number | null }

/** The app's limits: 150 m a side (`ModelSettings`), 1000 m², a 10 m ceiling, walls of 5 cm to 1 m. */
export const ROOM_SIZE_LIMITS = {
  side: { min: .3, max: 150 }, area: { min: .1, max: 1000 }, height: { min: 1, max: 10 }, thickness: { min: .05, max: 1 },
} as const;

const fail = (error: string): EditResult => ({ error });
const roomAt = (level: Level, index: number) => level.rooms.find(room => room.index === index);

export function roomSizes(level: Level, room: Room): RoomSizes {
  const extent = floorExtent(level, room);
  const thicknesses = [...new Set(edgeWalls(level, room).filter(Boolean).map(wall => Math.round(wall!.thickness * 1000)))];
  return { width: extent.width, depth: extent.depth, area: floorArea(level, room), height: level.height,
    thickness: thicknesses.length === 1 ? thicknesses[0] / 1000 : null };
}

/** The room a size edit made of `index`: the same index when the rebuild kept it, else the room over its old middle. */
function sameRoom(level: Level, index: number, middle: Vec2): Room | undefined {
  return roomAt(level, index) ?? level.rooms.find(room => containsPoint(room.polygon, middle));
}

function centroid(polygon: readonly Vec2[]): Vec2 {
  let area = 0, x = 0, z = 0;
  polygon.forEach((a, index) => {
    const b = polygon[(index + 1) % polygon.length], cross = a.x * b.z - b.x * a.z;
    area += cross; x += (a.x + b.x) * cross; z += (a.z + b.z) * cross;
  });
  if (Math.abs(area) < 1e-12) return { x: polygon.reduce((s, p) => s + p.x, 0) / polygon.length, z: polygon.reduce((s, p) => s + p.z, 0) / polygon.length };
  return { x: x / (3 * area), z: z / (3 * area) };
}

/**
 * Doors and windows stay where they stood in the world on a wall whose length changed (the app keeps them measured from
 * the end that did not move), within the wall.
 */
export function keepOpeningsInPlace(next: Level, base: Level): Level {
  const openings = next.openings.map(opening => {
    const before = base.walls.find(wall => wall.id === opening.wallId), wall = next.walls.find(value => value.id === opening.wallId);
    const was = base.openings.find(value => value.id === opening.id);
    if (!before || !wall || !was || (Math.abs(before.length - wall.length) < 1e-6 && Math.hypot(before.start.x - wall.start.x, before.start.z - wall.start.z) < 1e-6)) return opening;
    const centre = { x: before.start.x + before.dir.x * was.offset, z: before.start.z + before.dir.z * was.offset };
    const along = (centre.x - wall.start.x) * wall.dir.x + (centre.z - wall.start.z) * wall.dir.z;
    const offset = Math.max(opening.width / 2 + .02, Math.min(wall.length - opening.width / 2 - .02, along));
    return Math.abs(offset - opening.offset) < 1e-9 ? opening : { ...opening, offset };
  });
  return { ...next, openings };
}

/** Moves the side of the room across `axis` so its floor measures `target` that way. */
function setSide(level: Level, index: number, axis: 'x' | 'z', target: number): EditResult {
  const room = roomAt(level, index);
  if (!room) return fail(t('#RoomSizeRoomNotFound'));
  const extent = floorExtent(level, room), current = axis === 'x' ? extent.width : extent.depth, delta = target - current;
  if (Math.abs(delta) < 5e-4) return { level };
  const coord = (point: Vec2) => axis === 'x' ? point.x : point.z;
  const values = room.polygon.map(coord), middle = centroid(room.polygon);
  let lastError = t('#RoomSizeSideBlocked');
  for (const [line, sign] of [[Math.min(...values), -1], [Math.max(...values), 1]] as const) {
    // The side is every corner within 5 cm of the room's edge that way (`TrySetSize`'s tolerance).
    const moves = room.polygon.filter(point => Math.abs(coord(point) - line) < .05)
      .map(point => ({ from: point, to: axis === 'x' ? { x: point.x + sign * delta, z: point.z } : { x: point.x, z: point.z + sign * delta } }));
    const result = moveCorners(level, moves);
    if (!result.level) { lastError = result.error ?? lastError; continue; }
    const moved = sameRoom(result.level, index, { x: middle.x + (axis === 'x' ? sign * delta / 2 : 0), z: middle.z + (axis === 'z' ? sign * delta / 2 : 0) });
    const reached = moved && floorExtent(result.level, moved);
    if (reached && Math.abs((axis === 'x' ? reached.width : reached.depth) - target) < .01) return { level: keepOpeningsInPlace(result.level, level) };
  }
  return fail(lastError);
}

/** Scales the room about its middle so its floor comes to `target` m², the walls keeping their thickness. */
function setArea(level: Level, index: number, target: number): EditResult {
  const room = roomAt(level, index);
  if (!room) return fail(t('#RoomSizeRoomNotFound'));
  const current = floorArea(level, room);
  if (Math.abs(target - current) < 5e-3) return { level };
  if (current < 1e-6) return fail(t('#RoomSizeNoArea'));
  const insets = floorInsets(level, room), middle = centroid(room.polygon);
  const scaled = (k: number) => room.polygon.map(point => ({ x: middle.x + (point.x - middle.x) * k, z: middle.z + (point.z - middle.z) * k }));
  const areaAt = (k: number) => Math.abs(polygonArea(offsetPolygon(scaled(k), insets)));
  // The app's bisection (`TrySetArea`): 64 halvings between half and twice the plain square-root factor.
  const guess = Math.sqrt(target / current);
  let low = guess / 2, high = guess * 2;
  for (let i = 0; i < 64; i++) { const mid = (low + high) / 2; if (areaAt(mid) < target) low = mid; else high = mid; }
  const k = (low + high) / 2, corners = scaled(k);
  const result = moveCorners(level, room.polygon.map((from, i) => ({ from, to: corners[i] })));
  if (!result.level) return fail(result.error ?? t('#RoomSizeAreaFailed'));
  const walls = new Set(edgeWalls(level, room).filter(Boolean).map(wall => wall!.id));
  const inside = (item: Item) => !item.wallId && containsPoint(room.polygon, item.position);
  const next: Level = {
    ...result.level,
    // What stands in the room moves with it and keeps its size, as the app's furniture does.
    items: result.level.items.map(item => {
      const was = level.items.find(value => value.id === item.id);
      return was && inside(was) ? { ...item, position: { x: middle.x + (was.position.x - middle.x) * k, z: middle.z + (was.position.z - middle.z) * k } } : item;
    }),
    // A door or a window on a scaled wall keeps its place along it: its offset scales with the wall.
    openings: result.level.openings.map(opening => {
      const was = level.openings.find(value => value.id === opening.id);
      if (!was || !walls.has(opening.wallId)) return opening;
      const wall = result.level!.walls.find(value => value.id === opening.wallId);
      if (!wall) return opening;
      const offset = Math.max(opening.width / 2 + .02, Math.min(wall.length - opening.width / 2 - .02, was.offset * k));
      return { ...opening, offset };
    }),
  };
  const moved = sameRoom(next, index, middle);
  if (!moved || Math.abs(floorArea(next, moved) - target) > Math.max(.01, target * 1e-3)) return fail(t('#RoomSizeAreaBlocked'));
  return { level: next };
}

/** The ceiling of the whole floor: every room's, the walls of the old height, and what hangs from it. */
function setHeight(level: Level, target: number): EditResult {
  const old = level.height, delta = target - old;
  if (Math.abs(delta) < 5e-4) return { level };
  const walls = level.walls.map(wall => Math.abs(wall.height - old) < 1e-3 ? { ...wall, height: target } : wall);
  for (const opening of level.openings) {
    const wall = walls.find(value => value.id === opening.wallId);
    if (wall && opening.sill + opening.height > wall.height + 1e-6) return fail(t('#RoomSizeOpeningDoesNotFitHeight', target.toFixed(2)));
  }
  return { level: { ...level, height: target, walls,
    rooms: level.rooms.map(room => ({ ...room, height: target })),
    items: level.items.map(item => item.placement === 'ceiling' ? { ...item, elevation: Math.max(0, item.elevation + delta) } : item) } };
}

/** The room's walls to `target` thick, grown outward: each edge's axis moves out by half the growth, the floor stays. */
function setThickness(level: Level, index: number, target: number): EditResult {
  const room = roomAt(level, index);
  if (!room) return fail(t('#RoomSizeRoomNotFound'));
  const walls = edgeWalls(level, room);
  if (!walls.some(Boolean)) return fail(t('#RoomSizeNoWalls'));
  if (walls.every(wall => !wall || Math.abs(wall.thickness - target) < 5e-4)) return { level };
  const corners = offsetPolygon(room.polygon, walls.map(wall => wall ? -(target - wall.thickness) / 2 : 0));
  const moves = room.polygon.map((from, i) => ({ from, to: corners[i] })).filter(move => Math.hypot(move.to.x - move.from.x, move.to.z - move.from.z) > 1e-6);
  const moved = moveCorners(level, moves);
  if (!moved.level) return fail(moved.error ?? t('#RoomSizeThicknessFailed'));
  let next = moved.level;
  const again = sameRoom(next, index, centroid(room.polygon));
  if (!again) return fail(t('#RoomSizeThicknessFailed'));
  for (const wall of edgeWalls(next, again)) {
    if (!wall || Math.abs(wall.thickness - target) < 5e-4) continue;
    const result = applyWallProperties(next, wall.id, { thickness: target });
    if (!result.level) return fail(result.error ?? t('#RoomSizeThicknessFailed'));
    next = result.level;
  }
  return { level: keepOpeningsInPlace(next, level) };
}

/** The typed sizes applied in the popup's order; a value equal to the room's own changes nothing. */
export function resizeRoom(level: Level, index: number, values: RoomSizeValues): EditResult {
  const limits = ROOM_SIZE_LIMITS;
  const check = (value: number | undefined, range: { min: number; max: number }, message: TranslationKey) =>
    value === undefined || (Number.isFinite(value) && value >= range.min && value <= range.max) ? null : t(message, String(range.min), String(range.max));
  const invalid = check(values.width, limits.side, '#RoomSizeWidthRange') ?? check(values.depth, limits.side, '#RoomSizeLengthRange') ?? check(values.area, limits.area, '#RoomSizeAreaRange')
    ?? check(values.height, limits.height, '#RoomSizeCeilingHeightRange') ?? check(values.thickness, limits.thickness, '#RoomSizeWallThicknessRange');
  if (invalid) return fail(invalid);
  let next = level;
  const steps: Array<() => EditResult> = [
    () => values.thickness === undefined ? { level: next } : setThickness(next, index, values.thickness),
    () => values.width === undefined ? { level: next } : setSide(next, index, 'x', values.width),
    () => values.depth === undefined ? { level: next } : setSide(next, index, 'z', values.depth),
    () => values.area === undefined ? { level: next } : setArea(next, index, values.area),
    () => values.height === undefined ? { level: next } : setHeight(next, values.height),
  ];
  for (const step of steps) {
    const result = step();
    if (!result.level) return result;
    next = result.level;
  }
  return { level: next };
}
